import { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, playState } from "../../lib/api";
import { MobileTouchInput } from "../../engine/input/InputProvider";
import { MovementEngine } from "../../engine/MovementEngine";
import { MissionEngine, starsFor } from "../../engine/MissionEngine";
import { AnalyticsEngine } from "../../engine/AnalyticsEngine";
import { PhoneFrame, useElementSize } from "../../components/game/PhoneFrame";
import { VillageScene } from "../../components/game/VillageScene";
import { BaoInGame, rigHand } from "../../components/game/BaoInGame";
import { Projectile, Reticle, Weapon, aimInstruction } from "../../components/game/Projectile";
import { GameTarget } from "../../components/game/GameTarget";
import { GameHud } from "../../components/game/GameHud";
import { PainDialog } from "../../components/game/PainDialog";
import { Overlay, ElderIntro, Briefing, RestCard, LevelComplete } from "../../components/game/VillageOverlays";
import { useI18n } from "../../i18n";
import { NayaGreeting } from "../../components/game/NayaGreeting";

const INSTR = {
  water_bottle: "Tends le bras et touche la gourde",
  fruit: "Lève le bras et cueille la mangue",
  basket: "Fais glisser le panier jusqu'à l'étal",
  villager: "Lève la main et garde-la sur la cible",
  villager_bi: "Lève les deux mains et garde-les sur les cibles",
  branch: "Pose le doigt sur la main de Bao et monte doucement",
};
const SIDE_CUE = { left: "Main gauche", right: "Main droite", bilateral: "Les deux mains" };
const AIM_OBJECT = { water_bottle: "la gourde suspendue", fruit: "la mangue" };
const isThrow = (m) => m?.projectile && m.projectile !== "none";
const HINT = { drag: "Commence en touchant le panier", slow_drag: "Commence en touchant la main dorée" };

export default function VillageLevel() {
  const nav = useNavigate();
  const { t } = useI18n();
  const { sessionId, patientId } = playState.get();
  const [stageRef, size] = useElementSize();
  const [, force] = useReducer((x) => x + 1, 0);
  const eng = useRef(null);
  const busy = useRef(false);
  const [plan, setPlan] = useState(null);
  const [seeds, setSeeds] = useState(0);
  const [phase, setPhase] = useState("loading");
  const [hands, setHands] = useState({});
  const [msg, setMsg] = useState(null);
  const [fx, setFx] = useState(null);
  const [holdProg, setHoldProg] = useState({});
  const [results, setResults] = useState([]);
  const [painOpen, setPainOpen] = useState(false);
  const [paused, setPaused] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [shot, setShot] = useState(null);
  const [charge, setCharge] = useState(0);
  const [cheer, setCheer] = useState(0);
  const sizeRef = useRef(size);
  sizeRef.current = size;

  useEffect(() => {
    if (!sessionId) { nav("/patient"); return; }
    api.get(`/sessions/${sessionId}`).then(({ data }) => {
      if (!data.plan) return nav("/patient/calibration");
      if (["completed", "stopped"].includes(data.status)) return nav("/patient");
      const mv = new MovementEngine(data.plan.coordinate_mapping);
      eng.current = { me: new MissionEngine(data.plan, mv), an: new AnalyticsEngine(sessionId, "mobile_touch_simulated") };
      eng.current.an.setReported(data.pain_before, data.fatigue_before);
      setPlan(data.plan);
      setPhase("naya");
    });
    api.get(`/patients/${patientId}/game-progress`).then((r) => setSeeds(r.data.seeds));
  }, [sessionId, patientId, nav]);

  const maxS = (plan?.safety.max_duration_min || 20) * 60;
  useEffect(() => {
    if (phase === "loading" || phase === "level" || phase === "timeup" || paused) return;
    const id = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(id);
  }, [phase, paused]);
  useEffect(() => { if (plan && elapsed >= maxS && phase !== "level") setPhase("timeup"); }, [elapsed, maxS, plan, phase]);

  const advance = useCallback(() => {
    const { me, an } = eng.current;
    const key = me.mission.key;
    const r = me.next();
    busy.current = false;
    setMsg(null); setHands({}); setHoldProg({});
    if (r.missionDone) {
      setResults((o) => [...o, { key, stars: starsFor(an.forMission(key)) }]);
      setPhase(r.levelDone ? "level" : "rest");
    } else { me.beginTarget(); force(); }
  }, []);

  const handle = useCallback((ev) => {
    if (!ev || busy.current) return;
    const { me, an } = eng.current;
    const throwing = isThrow(me.mission) && ev.p && ["hit", "miss", "fail"].includes(ev.type);
    const flight = throwing ? 450 : 0;
    if (throwing) {
      const { w, h } = sizeRef.current, to = [ev.p.x * w, ev.p.y * h];
      const from = rigHand(sizeRef.current, ev.p.x < 0.5 ? "left" : "right", to);
      setShot({ from, to, kind: me.mission.projectile, k: Date.now() });
      setTimeout(() => setShot(null), flight + 100);
    }
    if (ev.type === "hit" || ev.type === "fail" || ev.type === "timeout") {
      busy.current = true;
      an.record(ev.attempt);
      const pos = me.target.positions[0];
      setTimeout(() => {
        if (ev.type === "hit") setCheer(Date.now());
        if (ev.type === "hit") setFx({ pos, text: ev.attempt.over_limit ? "Pas besoin d'aller si loin" : `${throwing ? "En plein dans le mille" : "Bravo"} ! ${Math.round(ev.attempt.accuracy)}%`, k: Date.now() });
        else setMsg("Bien essayé ! On continue.");
      }, flight);
      setTimeout(advance, 850 + flight);
    } else if (ev.type === "miss") setTimeout(() => setMsg(throwing ? "Raté ! Vise encore la cible." : "Presque ! Essaie encore."), flight);
    else if (ev.type === "too_quick") setMsg("Garde la visée un instant avant de relâcher.");
    else if (ev.type === "too_fast") setMsg("Doucement… monte lentement.");
    else if (ev.type === "hint") setMsg(HINT[me.mission.interaction]);
    else if (ev.type === "release" && me.mission.interaction === "hold") setMsg("Garde la main levée un peu plus longtemps.");
    force();
  }, [advance]);

  useEffect(() => {
    if (phase !== "play" || paused || painOpen || !stageRef.current) return;
    const { me } = eng.current;
    const input = new MobileTouchInput();
    const dropTimers = {};
    input.attach(stageRef.current);
    const unsub = input.subscribe((s) => {
      const side = s.x < 0.5 ? "left" : "right";
      const drop = () => setHands((h) => { const n = { ...h }; delete n[side]; return n; });
      clearTimeout(dropTimers[side]);
      if (s.phase === "end") { if (isThrow(me.mission)) dropTimers[side] = setTimeout(drop, 450); else drop(); }
      else setHands((h) => ({ ...h, [side]: { x: s.x, y: s.y } }));
      handle(me.handle(s));
    });
    let raf;
    const loop = () => {
      const now = performance.now();
      handle(me.tick(now));
      if (isThrow(me.mission) && me.s) {
        const c = me.s.aimSince != null ? Math.min(1, (now - me.s.aimSince) / me.mission.aim_hold_ms) : 0;
        setCharge((o) => (Math.abs(o - c) > 0.03 || (c === 0 && o !== 0) ? c : o));
      }
      if (me.mission?.interaction === "hold" && me.s) {
        const p = {};
        me.target?.positions.forEach((_, i) => { p[i] = me.s.done[i] !== undefined ? 1 : me.s.holds[i] ? Math.min(1, (now - me.s.holds[i].since) / me.mission.hold_ms) : 0; });
        setHoldProg(p);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => { unsub(); input.detach(); cancelAnimationFrame(raf); };
  }, [phase, paused, painOpen, handle, stageRef]);

  const me = eng.current?.me;
  const mission = phase === "play" || phase === "briefing" ? me?.mission : null;
  const target = phase === "play" ? me?.target : null;
  const total = plan?.missions.length || 1;
  const vitality = phase === "level" ? 1 : (results.length / total) * 0.55;
  const totals = { stars: results.reduce((n, r) => n + r.stars, 0), leaves: results.length };
  const end = (state) => nav("/patient/end", { state: { ...totals, ...state } });
  const begin = () => { me.beginTarget(); setPhase("play"); };

  return (
    <PhoneFrame>
      <div ref={stageRef} className="stage absolute inset-0" data-testid="village-stage">
        <VillageScene vitality={vitality} mission={mission?.key} />
        {target && <TargetLayer me={me} target={target} holdProg={holdProg} />}
        <BaoInGame size={size} hands={hands} cheer={cheer} weapon={target && isThrow(mission) ? mission.projectile : null} WeaponComp={Weapon} />
        {target && isThrow(mission) && Object.entries(hands).map(([sd, p]) => <Reticle key={sd} at={[p.x * size.w, p.y * size.h]} charge={charge} />)}
        {shot && <Projectile key={shot.k} kind={shot.kind} from={shot.from} to={shot.to} />}
        {fx && <div key={fx.k} className="float-up pointer-events-none absolute z-20 whitespace-nowrap rounded-full bg-white px-4 py-2 font-display text-lg font-extrabold text-[#0F7C7C] shadow-lg" style={{ left: `${fx.pos.x * 100}%`, top: `${fx.pos.y * 100}%` }}>{fx.text}</div>}
      </div>
      <GameHud onStop={() => end({ stopped: true })} seeds={seeds} onPain={() => setPainOpen(true)} remaining={maxS - elapsed} />
      {target && (
        <div className="pointer-events-none top-hud-offset absolute inset-x-3 z-20 rounded-3xl bg-white/90 p-4 shadow-lg backdrop-blur-md" data-testid="mission-instruction">
          <div className="flex items-center justify-between text-xs font-bold text-[#0F7C7C]">
            <span>MISSION {me.mi + 1}/{total} · {mission.name_fr}</span>
            <span data-testid="target-progress">{me.ti + 1}/{mission.targets.length}</span>
          </div>
          <p className="mt-1 font-display text-xl font-extrabold leading-tight text-[#0A2540]">
            {isThrow(mission) ? aimInstruction(mission.projectile, AIM_OBJECT[mission.key]) : INSTR[mission.key === "villager" && target.side === "bilateral" ? "villager_bi" : mission.key]}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <span data-testid="side-cue" className="rounded-full bg-[#0A2540] px-3 py-1 text-xs font-bold text-white">{SIDE_CUE[target.side]}</span>
            {msg && <span data-testid="game-feedback" className="text-sm font-semibold text-amber-700">{msg}</span>}
          </div>
        </div>
      )}
      {phase === "naya" && <Overlay><NayaGreeting level="village" onNext={() => setPhase("intro")} /></Overlay>}
      {phase === "intro" && <Overlay><ElderIntro onNext={() => setPhase("briefing")} /></Overlay>}
      {phase === "briefing" && mission && <Overlay><Briefing mission={mission} index={me.mi} total={total} onGo={begin} /></Overlay>}
      {phase === "rest" && <Overlay><RestCard result={results[results.length - 1]} restS={plan.safety.rest_interval_s} onNext={() => setPhase("briefing")} /></Overlay>}
      {phase === "level" && <Overlay dark={false}><LevelComplete totals={totals} t={t} onEnd={() => end({ levelCompleted: true })} /></Overlay>}
      {phase === "timeup" && (
        <Overlay>
          <div className="rounded-3xl bg-white p-6 text-center" data-testid="timeup-card">
            <p className="font-display text-2xl font-extrabold">Durée de séance atteinte</p>
            <p className="mt-2 text-slate-600">Bravo pour ta participation ! Bao se repose.</p>
            <button data-testid="timeup-end-button" onClick={() => end({})} className="mt-5 h-16 w-full rounded-full bg-brand text-lg font-extrabold text-white">Terminer la séance</button>
          </div>
        </Overlay>
      )}
      <PainDialog open={painOpen} onClose={() => setPainOpen(false)} sessionId={sessionId} analytics={eng.current?.an}
        paused={paused} setPaused={setPaused} onStop={() => end({ stopped: true })} />
    </PhoneFrame>
  );
}

const TargetLayer = ({ me, target, holdProg }) => {
  const m = me.mission, s = me.s;
  const lineX = target.side === "left" ? ["0%", "50%"] : target.side === "right" ? ["50%", "100%"] : ["0%", "100%"];
  return (
    <>
      <div data-testid="safe-limit-line" className="pointer-events-none absolute border-t-2 border-dashed border-white/80"
        style={{ top: `${target.limit_y * 100}%`, left: lineX[0], width: `calc(${lineX[1]} - ${lineX[0]})` }}>
        <span className="absolute -top-6 left-2 rounded-full bg-black/40 px-2 py-0.5 text-[10px] font-bold text-white">ZONE SÛRE · {target.safe_limit}{m.unit}</span>
      </div>
      {m.interaction === "tap" && <GameTarget kind={m.key} pos={target.positions[0]} testId={`mission-${m.key}-target`} />}
      {m.interaction === "drag" && (
        <>
          <GameTarget kind="stall" pos={target.positions[0]} testId="mission-basket-destination" pulse={false} />
          <GameTarget kind="basket" pos={s.pos} testId="mission-basket-target" pulse={!s.dragging} />
        </>
      )}
      {m.interaction === "hold" && target.positions.map((p, i) => (
        <GameTarget key={i} kind="villager" pos={p} progress={holdProg[i] || 0} testId={`mission-villager-target-${p.side}`} />
      ))}
      {m.interaction === "slow_drag" && (
        <>
          <svg className="pointer-events-none absolute inset-0 h-full w-full"><line x1={`${target.start.x * 100}%`} y1={`${target.start.y * 100}%`} x2={`${target.positions[0].x * 100}%`} y2={`${target.positions[0].y * 100}%`} stroke="#fff" strokeWidth="4" strokeDasharray="4 10" strokeLinecap="round" /></svg>
          <GameTarget kind="branch" pos={target.positions[0]} testId="mission-branch-target" pulse={false} />
          <GameTarget kind="hand" pos={s.pos} testId="mission-branch-hand" pulse={!s.dragging} />
        </>
      )}
    </>
  );
};

